import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Search, Mail, Phone, ArrowLeft, ExternalLink } from 'lucide-react';
import LoaderProgress from '../components/ui/loader-progress';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function ClusterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cluster, setCluster] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchCluster = async () => {
    try {
      const res = await api.get(`/cluster/${id}`);
      setCluster(res.data);
    } catch (error) {
      console.error('Error fetching cluster:', error);
      toast.error('Errore nel caricamento del cluster');
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/cluster/${id}/customers`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          orderBy,
          order
        }
      });
      setList(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error('Error fetching customers by cluster:', error);
      toast.error('Errore nel caricamento dei clienti del cluster');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCluster();
  }, [id]);

  useEffect(() => {
    fetchCustomers();
  }, [id, page, rowsPerPage, search, orderBy, order]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/cluster')}
          sx={{ textTransform: 'none' }}
        >
          Indietro
        </Button>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Clienti – Cluster {cluster?.label ? `"${cluster.label}"` : `#${id}`}
        </Typography>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={handleSearchChange}
            placeholder="Cerca per nominativo, email, telefono, ragione sociale..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 420 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'telephone'}
                    direction={orderBy === 'telephone' ? order : 'asc'}
                    onClick={() => handleRequestSort('telephone')}
                  >
                    Telefono
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'gender'}
                    direction={orderBy === 'gender' ? order : 'asc'}
                    onClick={() => handleRequestSort('gender')}
                  >
                    Sesso
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_nascita'}
                    direction={orderBy === 'data_nascita' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_nascita')}
                  >
                    Data di nascita
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Creato il
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    Nessun cliente trovato
                  </TableCell>
                </TableRow>
              ) : (
                list.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {c.ragione_sociale ||
                           [c.name, c.surname].filter(Boolean).join(' ') ||
                           '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {c.email ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                          <Mail size={14} />
                          {c.email}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {c.telephone ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                          <Phone size={14} />
                          {c.telephone}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {c.gender || c.sesso || '-'}
                    </TableCell>
                    <TableCell>
                      {c.data_nascita
                        ? new Date(c.data_nascita).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell>{c.created_at ? new Date(c.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Apri cliente">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/customer/${c.id}`)}
                        >
                          <ExternalLink size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
